import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listManufacturers, listModels, newIdempotencyKey, submitClaim, toApiError } from '../../services/pocApi';
import useApiResource from '../../components/poc/useApiResource';
import { DecisionSummary } from '../../components/poc/DecisionDetails';
import {
    DetailList, Disclaimer, ErrorNotice, Field, LoadingState, PageHeader, Panel, POC_LINKS, SubNav,
} from '../../components/poc/PocUi';
import {
    COMMON_CATEGORIES, COVERAGE_TYPES, DISTANCE_UNITS, MODEL_YEAR_AFTER_REPAIR_YEAR, PATTERNS, formatAmount, isRealDate,
    manufacturerName, modelLabel, modelYearOptions, normalizeAmount, splitCodes,
} from '../../components/poc/pocFormat';

const OTHER = '__OTHER__';
const EMPTY_FORM = {
    manufacturer_code: '', model_code: '', model_year: '', vin: '', warranty_start_date: '', repair_date: '', odometer_reading: '',
    distance_unit: 'KM', coverage_type: 'BASIC', category_choice: '', other_category: '', part_codes: '',
    sublet_codes: '', claim_amount: '', currency: 'USD',
};

/**
 * Builds the POST /poc-claims body (claim facts only - the request schema
 * forbids any decision or status field) and collects validation errors.
 * Empty optional facts are sent as null: the backend refers them to manual
 * review instead of rejecting the request.
 *
 * Phase 5: when the manufacturer has catalogue models (`modelRequired`) a model
 * must be chosen, because policies can be model-specific and a claim without
 * one would be referred for manual review. A chosen model also needs its model
 * year, which is sent only with a model.
 */
export function buildClaimPayload(form, { modelRequired = false } = {}) {
    const errors = {};
    const text = (v) => String(v ?? '').trim();
    const orNull = (v) => (text(v) === '' ? null : text(v));

    if (!form.manufacturer_code) errors.manufacturer_code = 'Select a manufacturer.';
    else if (modelRequired && !form.model_code) {
        errors.model_code = 'Select a model. Warranty policies can depend on the model.';
    }

    let modelYear = null;
    if (form.model_code) {
        const yearText = text(form.model_year);
        const repair = orNull(form.repair_date);
        if (!yearText) errors.model_year = 'Select the model year.';
        else if (!/^\d{4}$/.test(yearText) || Number(yearText) < 1900 || Number(yearText) > 2100) {
            errors.model_year = 'Enter a four-digit model year.';
        } else if (repair && isRealDate(repair) && Number(yearText) > Number(repair.slice(0, 4)) + MODEL_YEAR_AFTER_REPAIR_YEAR) {
            errors.model_year = `The model year cannot be later than the repair year + ${MODEL_YEAR_AFTER_REPAIR_YEAR}.`;
        } else {
            modelYear = Number(yearText);
        }
    }

    const vin = orNull(form.vin)?.toUpperCase() ?? null;
    if (vin && !PATTERNS.vin.test(vin)) errors.vin = 'A VIN has 17 letters and digits (no I, O or Q).';

    for (const field of ['warranty_start_date', 'repair_date']) {
        const value = orNull(form[field]);
        if (value && !isRealDate(value)) errors[field] = 'Enter a valid date (YYYY-MM-DD).';
    }

    let odometer = null;
    const odometerText = text(form.odometer_reading).replace(/,/g, '');
    if (odometerText) {
        if (!/^\d{1,8}$/.test(odometerText) || Number(odometerText) > 10000000) {
            errors.odometer_reading = 'Enter a whole number between 0 and 10,000,000.';
        } else {
            odometer = Number(odometerText);
        }
    }
    if (!DISTANCE_UNITS.includes(form.distance_unit)) errors.distance_unit = 'Select a distance unit.';
    if (!COVERAGE_TYPES.includes(form.coverage_type)) errors.coverage_type = 'Select a coverage type.';

    let category = null;
    if (form.category_choice === OTHER) {
        category = text(form.other_category).toUpperCase();
        if (!PATTERNS.category.test(category)) {
            errors.other_category = 'Use an upper-case code: a letter, then letters, digits or underscores (max 64).';
        }
    } else if (form.category_choice) {
        category = form.category_choice;
    }

    const codes = {};
    for (const [field, max] of [['part_codes', 100], ['sublet_codes', 50]]) {
        codes[field] = splitCodes(form[field]);
        if (codes[field].length > max) errors[field] = `Enter at most ${max} codes.`;
        else if (codes[field].some((c) => !PATTERNS.code.test(c))) {
            errors[field] = 'Codes are 1-64 letters, digits, ".", "_" or "-", separated by commas.';
        }
    }

    let amount = null;
    if (text(form.claim_amount)) {
        amount = normalizeAmount(form.claim_amount);
        if (amount === null) errors.claim_amount = 'Enter a non-negative amount with up to two decimals, e.g. 3500.00.';
    }
    const currency = orNull(form.currency);
    if (currency && !PATTERNS.currency.test(currency)) errors.currency = 'Use a three-letter currency code.';

    const payload = {
        manufacturer_code: orNull(form.manufacturer_code),
        model_code: orNull(form.model_code),
        ...(form.model_code ? { model_year: modelYear } : {}),
        vin,
        warranty_start_date: orNull(form.warranty_start_date),
        repair_date: orNull(form.repair_date),
        odometer_reading: odometer,
        distance_unit: form.distance_unit,
        coverage_type: form.coverage_type,
        component_category: category,
        part_codes: codes.part_codes,
        sublet_codes: codes.sublet_codes,
        claim_amount: amount,
        currency,
    };
    return { payload, errors };
}

/** True when the catalogue lists models for the manufacturer; then a model is required (decision D-1). */
const hasModels = (manufacturerCode, models) => Boolean(manufacturerCode && !models.loading && !models.error && models.data?.length);

function ModelSelect({ manufacturerCode, models, value, onChange, error }) {
    const items = models.data || [];
    const required = hasModels(manufacturerCode, models);
    let hint = 'Required. Warranty policies can be specific to a model, so a claim without one is referred for manual review.';
    if (!manufacturerCode) hint = 'Select a manufacturer first.';
    else if (models.error) hint = 'Models could not be loaded. You can still submit a manufacturer-level claim.';
    else if (!models.loading && !items.length) hint = 'No models are currently configured for this manufacturer. The claim is evaluated at manufacturer level.';
    let placeholder = 'No specific model (manufacturer level)';
    if (models.loading && manufacturerCode) placeholder = 'Loading models…';
    else if (required) placeholder = 'Select a model';
    return (
        <Field label="Model" hint={hint} required={required} error={error}>
            <select className="form-select" value={value} onChange={onChange} disabled={!manufacturerCode || models.loading}>
                <option value="">{placeholder}</option>
                {items.map((m) => (
                    <option key={m.model_code} value={m.model_code}>{(m.model_name || '').trim() || m.model_code} ({m.model_code})</option>
                ))}
            </select>
        </Field>
    );
}

function ModelYearSelect({ model, repairDate, value, onChange, error }) {
    const years = modelYearOptions(model?.release_year, repairDate);
    return (
        <Field label="Model Year" required error={error}
               hint={`From the model's indicative first year${model?.release_year ? ` (${model.release_year})` : ''} to the repair year + ${MODEL_YEAR_AFTER_REPAIR_YEAR}. Some policies apply to specific model years only.`}>
            <select className="form-select" value={value} onChange={onChange}>
                <option value="">Select the model year</option>
                {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
        </Field>
    );
}

function SubmissionResult({ result, manufacturers, onAnother }) {
    const headingRef = useRef(null);
    useEffect(() => headingRef.current?.focus(), []);
    const { claim, status } = result;
    return (
        <div className="space-y-6" role="status" aria-live="polite">
            <Panel title={<span ref={headingRef} tabIndex={-1} className="focus:outline-none">Claim {claim.claim_id} — Decision</span>}>
                <div className="space-y-6">
                    {status === 200 && (
                        <p className="text-sm border-l-2 border-[#3B6FB6] pl-3">
                            This submission had already been recorded. The original claim is shown; no new claim was created.
                        </p>
                    )}
                    <DetailList items={[
                        ['Claim', claim.claim_id],
                        ['Manufacturer', manufacturerName(manufacturers, claim.manufacturer_code)],
                        ['Model', modelLabel(claim, 'Manufacturer level')],
                        ['Model year', claim.model_year ?? '—'],
                        ['Component', claim.component_category],
                        ['Claim amount', formatAmount(claim.claim_amount, claim.currency)],
                        ['Repair date', claim.repair_date],
                    ]} />
                    <DecisionSummary decision={claim.latest_decision} />
                </div>
            </Panel>
            <div className="flex flex-wrap gap-3">
                <Link to={`/poc-claims/${claim.claim_id}`} className="btn btn-primary">View Claim Details</Link>
                <Link to={`/poc-claims/${claim.claim_id}#history`} className="btn btn-secondary">View Decision History</Link>
                <button type="button" className="btn btn-secondary" onClick={onAnother}>Submit Another Claim</button>
                <Link to="/poc-claims" className="btn btn-secondary">Return to Dashboard</Link>
            </div>
        </div>
    );
}

export default function PocClaimSubmit() {
    const manufacturers = useApiResource((signal) => listManufacturers(signal), []);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    // The key belongs to one logical submission: it is reused while the body is unchanged
    // (a retry after a timeout or network error) and replaced when the body changes.
    const attempt = useRef(null);
    const inFlight = useRef(false);

    const models = useApiResource(
        (signal) => (form.manufacturer_code ? listModels(form.manufacturer_code, signal) : Promise.resolve([])),
        [form.manufacturer_code],
    );
    const selectedModel = (models.data || []).find((m) => m.model_code === form.model_code);

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    const setManufacturer = (e) => setForm((f) => ({ ...f, manufacturer_code: e.target.value, model_code: '', model_year: '' }));
    const setModel = (e) => setForm((f) => ({ ...f, model_code: e.target.value, model_year: '' }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (inFlight.current) return;                  // double-click / repeated Enter
        const { payload, errors: found } = buildClaimPayload(form, { modelRequired: hasModels(form.manufacturer_code, models) });
        setErrors(found);
        setSubmitError(null);
        if (Object.keys(found).length) return;

        const body = JSON.stringify(payload);
        if (!attempt.current || attempt.current.body !== body) {
            attempt.current = { key: newIdempotencyKey(), body };
        }
        inFlight.current = true;
        setSubmitting(true);
        try {
            const outcome = await submitClaim(payload, attempt.current.key);
            attempt.current = null;
            setResult(outcome);
        } catch (err) {
            const apiError = toApiError(err);
            if (apiError.kind === 'conflict') attempt.current = null;   // the next attempt is a new request
            if (apiError.fieldErrors) setErrors((prev) => ({ ...prev, ...apiError.fieldErrors }));
            setSubmitError(apiError);
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };

    const another = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setSubmitError(null);
        setResult(null);
    };

    const catalogue = manufacturers.data || [];
    return (
        <div className="space-y-8 max-w-5xl mx-auto">
            <SubNav label="POC claim management" links={POC_LINKS} />
            <PageHeader title="Submit POC Warranty Claim"
                        subtitle="The claim is saved and evaluated by the backend against the active warranty policies. The decision is calculated on the server, never in this form." />
            <Disclaimer />

            {result ? (
                <SubmissionResult result={result} manufacturers={catalogue} onAnother={another} />
            ) : (
                <form onSubmit={onSubmit} noValidate className="space-y-8" aria-busy={submitting}>
                    {manufacturers.loading && <LoadingState label="Loading manufacturers…" />}
                    <ErrorNotice error={manufacturers.error} onRetry={manufacturers.reload} title="Manufacturer catalogue unavailable" />

                    <Panel title="Vehicle Information">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label="Manufacturer" required error={errors.manufacturer_code}
                                   hint="From the ICMS manufacturer master. The manufacturer code is submitted.">
                                <select className="form-select" value={form.manufacturer_code} onChange={setManufacturer} disabled={!catalogue.length}>
                                    <option value="">Select a manufacturer</option>
                                    {catalogue.map((m) => (
                                        <option key={m.manufacturer_code} value={m.manufacturer_code}>
                                            {m.manufacturer_name} ({m.manufacturer_code}){!m.is_active ? ' — legacy' : ''}{!m.has_active_policy ? ' — no active policy' : ''}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <ModelSelect manufacturerCode={form.manufacturer_code} models={models} value={form.model_code}
                                         onChange={setModel} error={errors.model_code} />
                            {form.model_code && (
                                <ModelYearSelect model={selectedModel} repairDate={form.repair_date} value={form.model_year}
                                                 onChange={set('model_year')} error={errors.model_year} />
                            )}
                            <Field label="VIN" error={errors.vin} hint="Optional. Synthetic demonstration VINs only.">
                                <input type="text" className="form-input" value={form.vin} onChange={set('vin')} maxLength={17} autoComplete="off" />
                            </Field>
                        </div>
                    </Panel>

                    <Panel title="Warranty and Repair">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label="Warranty Start Date" error={errors.warranty_start_date}
                                   hint="The vehicle's in-service date. If left empty the claim is referred for manual review; it is never derived from the purchase year.">
                                <input type="date" className="form-input" value={form.warranty_start_date} onChange={set('warranty_start_date')} />
                            </Field>
                            <Field label="Repair Date" error={errors.repair_date}>
                                <input type="date" className="form-input" value={form.repair_date} onChange={set('repair_date')} />
                            </Field>
                            <Field label="Current Odometer Reading" error={errors.odometer_reading}>
                                <input type="text" inputMode="numeric" className="form-input" value={form.odometer_reading} onChange={set('odometer_reading')} />
                            </Field>
                            <Field label="Distance Unit" error={errors.distance_unit}
                                   hint="POC policies are configured in KM. Readings are never converted between units.">
                                <select className="form-select" value={form.distance_unit} onChange={set('distance_unit')}>
                                    {DISTANCE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </Field>
                        </div>
                    </Panel>

                    <Panel title="Claim Information">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label="Coverage Type" error={errors.coverage_type}>
                                <select className="form-select" value={form.coverage_type} onChange={set('coverage_type')}>
                                    {COVERAGE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </Field>
                            <Field label="Component Category"
                                   hint="Categories not covered by the applicable policy lead to a rejection; an empty category is referred for manual review.">
                                <select className="form-select" value={form.category_choice} onChange={set('category_choice')}>
                                    <option value="">Not specified</option>
                                    {COMMON_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    <option value={OTHER}>Other category…</option>
                                </select>
                            </Field>
                            {form.category_choice === OTHER && (
                                <Field label="Other Category Code" error={errors.other_category} hint="Upper-case code, for example PAINT.">
                                    <input type="text" className="form-input" value={form.other_category} onChange={set('other_category')} maxLength={64} />
                                </Field>
                            )}
                            <Field label="Part Codes" error={errors.part_codes} hint="Optional, comma-separated.">
                                <input type="text" className="form-input" value={form.part_codes} onChange={set('part_codes')} />
                            </Field>
                            <Field label="Sublet Codes" error={errors.sublet_codes} hint="Optional, comma-separated.">
                                <input type="text" className="form-input" value={form.sublet_codes} onChange={set('sublet_codes')} />
                            </Field>
                            <Field label="Claim Amount" error={errors.claim_amount} hint="Decimal amount, for example 3500.00.">
                                <input type="text" inputMode="decimal" className="form-input" value={form.claim_amount} onChange={set('claim_amount')} />
                            </Field>
                            <Field label="Currency" error={errors.currency} hint="POC policies use USD. Amounts are never converted.">
                                <select className="form-select" value={form.currency} onChange={set('currency')}>
                                    <option value="USD">USD</option>
                                </select>
                            </Field>
                        </div>
                    </Panel>

                    <ErrorNotice error={submitError} title="The claim was not submitted" />
                    <div className="flex flex-wrap gap-3">
                        <button type="submit" className="btn btn-primary" disabled={submitting || !catalogue.length}>
                            {submitting ? 'Submitting…' : 'Submit Claim'}
                        </button>
                        <Link to="/poc-claims" className="btn btn-secondary">Cancel</Link>
                    </div>
                </form>
            )}
        </div>
    );
}
